import { useCallback, useEffect, useRef, type KeyboardEvent } from 'react';

const TYPEAHEAD_RESET_MS = 800;

type FirstLetterTypeaheadItem<T extends string> = {
  value: T;
  label: string;
  disabled?: boolean;
};

type UseFirstLetterTypeaheadParams<T extends string> = {
  items: readonly FirstLetterTypeaheadItem<T>[];
  value: T;
  onValueChange: (value: T) => void;
  enabled?: boolean;
};

const getFirstLetter = (label: string) => label.trim().charAt(0).toLocaleLowerCase();

export const useFirstLetterTypeahead = <T extends string>({
  items,
  value,
  onValueChange,
  enabled = false,
}: UseFirstLetterTypeaheadParams<T>) => {
  const tabElementsRef = useRef(new Map<string, HTMLElement>());
  const lastLetterRef = useRef<string | null>(null);
  const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const registerTabElement = useCallback((itemValue: T, element: HTMLElement | null) => {
    if (element) {
      tabElementsRef.current.set(itemValue, element);
    } else {
      tabElementsRef.current.delete(itemValue);
    }
  }, []);

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (!enabled || event.ctrlKey || event.metaKey || event.altKey) {
        return;
      }

      const { key } = event;
      if (key.length !== 1 || !/\p{L}/u.test(key)) {
        return;
      }

      const letter = key.toLocaleLowerCase();
      const matches = items.filter(item => !item.disabled && getFirstLetter(item.label) === letter);

      if (matches.length === 0) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const currentIndex = matches.findIndex(item => item.value === value);
      const shouldCycle =
        lastLetterRef.current === letter && currentIndex >= 0 && matches.length > 1;
      const nextIndex = shouldCycle ? (currentIndex + 1) % matches.length : 0;
      const next = matches[nextIndex];

      lastLetterRef.current = letter;
      if (resetTimeoutRef.current !== null) {
        clearTimeout(resetTimeoutRef.current);
      }
      resetTimeoutRef.current = setTimeout(() => {
        lastLetterRef.current = null;
        resetTimeoutRef.current = null;
      }, TYPEAHEAD_RESET_MS);

      onValueChange(next.value);
      // Defer focus to a microtask so the tab element for `next.value` exists after the value change has been applied.
      queueMicrotask(() => tabElementsRef.current.get(next.value)?.focus());
    },
    [enabled, items, onValueChange, value]
  );

  // Clear any pending reset timer on unmount so it can't fire after the component is gone.
  useEffect(
    () => () => {
      if (resetTimeoutRef.current !== null) {
        clearTimeout(resetTimeoutRef.current);
      }
    },
    []
  );

  return { onKeyDown, registerTabElement };
};
